import { AxiosInstance, AxiosResponse } from "axios";

/**
 * List the tenant's Extended PoPs: one row per site (85-), each with its
 * CPEs (80-/83-) under it, active first.
 */
export function fetchExtenderPopList(
  api: AxiosInstance,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops?tenantId=${tenantId}&includeChildServices=true`
  );
}

/**
 * Detail of one Extended PoP: the site with its CPEs, each with its role,
 * links, carrier legs and subscription term.
 */
export function fetchExtenderPop(
  api: AxiosInstance,
  extenderPopId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}?tenantId=${tenantId}`
  );
}

/**
 * The logical services the Extended PoP delivers (Internet, DCI, Cloud,
 * Firewall), each with its port, CPE and live status (CTX-797).
 */
export function fetchExtenderPopConnectionsAPI(
  api: AxiosInstance,
  extenderPopId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}/connections?tenantId=${tenantId}`
  );
}

/**
 * Bandwidth utilization for ONE link of the Extender PoP (a backbone
 * uplink or the HA interconnect), selected by `linkId` from the detail's
 * `links`. Same query modes as the Ports performance endpoint.
 */
export function fetchExtenderPopPerformanceAPI(
  api: AxiosInstance,
  extenderPopId: string,
  linkId: string,
  interval: string,
  tenantId: number,
  timeRange?: string,
  startDate?: string,
  endDate?: string,
  maxPoints?: number
): Promise<AxiosResponse> {
  const params = new URLSearchParams({
    tenantId: String(tenantId),
    linkId,
    interval,
  });
  if (startDate && endDate) {
    params.set("start", startDate);
    params.set("end", endDate);
  } else if (timeRange) {
    params.set("timeRange", timeRange);
  }
  if (maxPoints) {
    params.set("maxPoints", String(maxPoints));
  }
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}/performance?${params.toString()}`
  );
}

/** A leg declared by hand on an interface of a CPE's router (CTX-830). */
export interface ManualLegBody {
  cpeId: number;
  portAllocationId: number;
  carrierCode: string;
  circuitId?: string;
  serviceRequestId?: string;
  medium?: "fiber" | "satellite";
}

/**
 * Declare the Carrier Access (84-) of the site and claim its legs. Global
 * Admin only. `legIds` left out claims every free leg of the site.
 */
export function declareExtenderPopCarrierAccessAPI(
  api: AxiosInstance,
  extenderPopId: number,
  tenantId: number,
  body: {
    name?: string;
    accessType?: string;
    legIds?: number[];
    manualLegs?: ManualLegBody[];
  }
): Promise<AxiosResponse> {
  return api.post(
    `/kontroledge/extender-pops/${extenderPopId}/carrier-access?tenantId=${tenantId}`,
    body
  );
}

/** Claim a derived leg (`{ legId }`) or add a manual one. Global Admin only. */
export function addExtenderPopCarrierAccessLegAPI(
  api: AxiosInstance,
  extenderPopId: number,
  carrierAccessId: number,
  tenantId: number,
  body: { legId: number } | ManualLegBody
): Promise<AxiosResponse> {
  return api.post(
    `/kontroledge/extender-pops/${extenderPopId}/carrier-access/${carrierAccessId}/legs?tenantId=${tenantId}`,
    body
  );
}

/** Delete a manual leg or release a derived one. Global Admin only. */
export function removeExtenderPopCarrierAccessLegAPI(
  api: AxiosInstance,
  extenderPopId: number,
  carrierAccessId: number,
  legId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.delete(
    `/kontroledge/extender-pops/${extenderPopId}/carrier-access/${carrierAccessId}/legs/${legId}?tenantId=${tenantId}`
  );
}

/** Live interfaces of a CPE's router, to place a manual leg. Global Admin only. */
export function fetchExtenderPopCpeInterfacesAPI(
  api: AxiosInstance,
  extenderPopId: number,
  cpeId: number,
  tenantId: number
): Promise<AxiosResponse> {
  return api.get(
    `/kontroledge/extender-pops/${extenderPopId}/cpes/${cpeId}/interfaces?tenantId=${tenantId}`
  );
}

/** What a leg's medium / priority PATCH carries; null clears the value. */
export interface LegCaptureBody {
  medium?: "fiber" | "satellite" | null;
  priority?: "primary" | "secondary" | null;
}

/**
 * Capture the medium (CTX-800) and/or the priority of a carrier leg of the
 * site. Global Admin only.
 */
export function updateExtenderPopLegAPI(
  api: AxiosInstance,
  extenderPopId: number,
  legId: number,
  tenantId: number,
  body: LegCaptureBody
): Promise<AxiosResponse> {
  return api.patch(
    `/kontroledge/extender-pops/${extenderPopId}/legs/${legId}?tenantId=${tenantId}`,
    body
  );
}

/** What the API answers after tying a CPE device to its router. */
export interface CpeRouterAssignment {
  cpeId: number;
  serviceId: string;
  router: { id: number; hostname: string; datacenterId: string | null };
  /** False when the device already had this router. */
  changed: boolean;
}

/**
 * Tie a CPE device to its router by hostname (CTX-829). Global Admin only.
 * The API refuses, with the reason, a hostname that is not in the inventory
 * (404), a router of another CPE or one in another datacenter than the
 * site's other devices (409).
 */
export function assignExtenderPopCpeRouterAPI(
  api: AxiosInstance,
  extenderPopId: number,
  cpeId: number,
  tenantId: number,
  hostname: string
): Promise<AxiosResponse> {
  return api.put(
    `/kontroledge/extender-pops/${extenderPopId}/cpes/${cpeId}/router?tenantId=${tenantId}`,
    { hostname }
  );
}

/**
 * Any subset of the site's editable fields: its name and its location. Any
 * tenant user of the hierarchy may send it. Latitude and longitude travel
 * together, both numbers or both null.
 */
export interface ExtenderPopPatchBody {
  name?: string;
  locationName?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export function updateExtenderPopAPI(
  api: AxiosInstance,
  extenderPopId: number,
  tenantId: number,
  body: ExtenderPopPatchBody
): Promise<AxiosResponse> {
  return api.patch(
    `/kontroledge/extender-pops/${extenderPopId}?tenantId=${tenantId}`,
    body
  );
}

/**
 * Up to five matches for an address, looked up server side on OpenStreetMap
 * Nominatim. An empty list means not found; 502 means the lookup is down.
 */
export function geocodeExtenderPopAddressAPI(
  api: AxiosInstance,
  query: string,
  tenantId: number
): Promise<AxiosResponse> {
  const params = new URLSearchParams({ q: query, tenantId: String(tenantId) });
  return api.get(`/kontroledge/extender-pops/geocode?${params.toString()}`);
}
